import { Button, CommandInput, CommandItem } from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import { ArrowRightIcon } from '@ttab/elephant-ui/icons'
import { useEffect, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

interface FreeTextFilterProps {
  value: string
  onChange: (value: string) => void
  filterType: 'freetext' | 'filterOptions'
  autoFocus?: boolean
}

export const FreeTextFilter = ({ value, onChange, filterType, autoFocus = true }: FreeTextFilterProps): JSX.Element => {
  const [selected, setSelected] = useState(true)
  const [draft, setDraft] = useState(value)
  const { t } = useTranslation('shared')

  useEffect(() => {
    setDraft(value)
  }, [value])

  const isFreetext = filterType === 'freetext'
  const pending = isFreetext && draft.trim() !== value

  const submit = () => {
    if (pending) {
      onChange(draft.trim())
    }
  }

  return (
    <CommandItem
      forceMount
      value='freetext'
      onKeyUp={(e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          setSelected(e.currentTarget.getAttribute('data-selected') === 'true')
        }
      }}
      className={cn('pb-0 mb-1.5 bg-white! dark:bg-background!',
        isFreetext && 'data-[selected=true]:shadow-[inset_0_0px_3px_rgba(0,0,0,0.5)] dark:data-[selected=true]:shadow-[inset_0_0px_3px_rgba(255,255,255,1)]')}
    >
      <div className='relative w-full'>
        <CommandInput
          value={draft}
          onValueChange={(v) => {
            setDraft(v)
            if (!isFreetext) {
              onChange(v)
            }
          }}
          onKeyDown={(e) => {
            // Focus stays in the input while the arrow keys move through the menu,
            // so Enter belongs to this field only while it is the highlighted item
            const highlighted = e.currentTarget.closest('[cmdk-item]')?.getAttribute('data-selected') === 'true'
            if (isFreetext && highlighted && e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault()
              submit()
            }
          }}
          placeholder={isFreetext ? t('toolbar.freeText') : t('toolbar.altSearch')}
          className={cn('h-9', isFreetext && 'pr-7')}
          autoFocus={autoFocus}
          readOnly={isFreetext && !selected}
        />
        {pending && (
          <Button
            type='button'
            size='icon'
            tabIndex={-1}
            aria-label={t('toolbar.freeTextSearch')}
            className='absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6'
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation()
              submit()
            }}
          >
            <ArrowRightIcon size={14} strokeWidth={2} />
          </Button>
        )}
      </div>
    </CommandItem>
  )
}
